import React, { useEffect, useState } from "react";
import { Store, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDateTime } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge } from "@/components/common";

const STATUS = { open: ["Berjalan", "bg-sky-100 text-sky-700"], partial: ["Sebagian", "bg-amber-100 text-amber-700"], closed: ["Selesai", "bg-slate-100 text-slate-600"] };

export default function Consignment() {
  const { branches, can } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setList(await api.get("/consignments")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <PageHeader title="Titip Pasar (Konsinyasi)" subtitle={`${list.length} pengiriman`} icon={Store}
        actions={can("consignment", "create") && <Button onClick={() => setCreating(true)} data-testid="add-consign-btn"><Plus size={16} /> Kirim Titipan</Button>} />

      {loading ? <Loading /> : !list.length ? <EmptyState icon={Store} title="Belum ada titipan" /> : (
        <div className="space-y-2">
          {list.map((c) => {
            const [label, cls] = STATUS[c.status] || ["-", ""];
            return (
              <Card key={c.id} className="p-4 cursor-pointer hover:border-primary flex flex-wrap items-start justify-between gap-3" onClick={() => setDetail(c.id)} data-testid={`consign-${c.id}`}>
                <div>
                  <div className="font-medium text-slate-800">{c.consignment_no}</div>
                  <div className="text-xs text-slate-400">{c.partner_name || c.partner_branch_name} · {(c.items || []).length} item · {fmtDateTime(c.created_at)}</div>
                </div>
                <div className="text-right">
                  <Badge className={cls}>{label}</Badge>
                  {c.total_settled != null && <div className="text-sm font-semibold text-emerald-600 mt-1">Laku {rupiah(c.total_settled)}</div>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {creating && <SendModal branches={branches} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {detail && <DetailModal id={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </>
  );
}

function SendModal({ branches, onClose, onSaved }) {
  const [source, setSource] = useState("");
  const [partner, setPartner] = useState("");
  const [materials, setMaterials] = useState([]);
  const [lines, setLines] = useState([{ item_id: "", qty: 1, price: 0 }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/materials").then(setMaterials); }, []);
  const consignBranches = branches.filter((b) => b.type === "consignment" || b.type === "mobile");

  const save = async () => {
    if (!source || !partner) return toast.error("Pilih sumber & mitra");
    const items = lines.filter((l) => l.item_id).map((l) => ({ item_id: l.item_id, qty: Number(l.qty), price: Number(l.price) }));
    if (!items.length) return toast.error("Tambah item");
    setBusy(true);
    try { await api.post("/consignments", { source_branch_id: source, partner_branch_id: partner, items }); toast.success("Titipan dikirim"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Kirim Titipan" maxWidth="max-w-md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Sumber</span><select value={source} onChange={(e) => setSource(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300"><option value="">Pilih...</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
          <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Mitra/Unit</span><select value={partner} onChange={(e) => setPartner(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300"><option value="">Pilih...</option>{consignBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1"><span className="text-sm font-medium text-slate-600">Item</span><Button size="sm" variant="subtle" onClick={() => setLines((l) => [...l, { item_id: "", qty: 1, price: 0 }])}><Plus size={14} /></Button></div>
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <select value={l.item_id} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, item_id: e.target.value } : x))} className="flex-1 h-10 px-2 rounded-xl border border-slate-300 text-sm"><option value="">Bahan...</option>{materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
              <input type="number" value={l.qty} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} placeholder="Qty" className="w-16 h-10 px-2 rounded-xl border border-slate-300" />
              <input type="number" value={l.price} onChange={(e) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, price: e.target.value } : x))} placeholder="Harga" className="w-24 h-10 px-2 rounded-xl border border-slate-300" />
              <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="w-8 text-rose-500"><X size={16} /></button>
            </div>
          ))}
        </div>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="consign-save">{busy ? "Menyimpan..." : "Kirim"}</Button>
      </div>
    </Modal>
  );
}

function DetailModal({ id, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [sold, setSold] = useState({});
  const [busy, setBusy] = useState(false);
  const load = async () => { const d = await api.get(`/consignments/${id}`); setC(d); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const settle = async () => {
    const items = (c.items || []).map((it) => ({ item_id: it.item_id, qty_sold: Number(sold[it.item_id] || 0) })).filter((x) => x.qty_sold > 0);
    if (!items.length) return toast.error("Isi jumlah terjual");
    setBusy(true);
    try { await api.post(`/consignments/${id}/settle`, { items, method: "cash" }); toast.success("Setoran dicatat"); await load(); onChanged(); setSold({}); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const doReturn = async () => {
    const items = (c.items || []).map((it) => ({ item_id: it.item_id, qty_returned: Number(it.qty_remaining ?? it.qty ?? 0) }));
    setBusy(true);
    try { await api.post(`/consignments/${id}/return`, { items, close: true }); toast.success("Retur dicatat & titipan ditutup"); await load(); onChanged(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={c?.consignment_no || "Titipan"} maxWidth="max-w-lg">
      {!c ? <Loading /> : (
        <div className="space-y-4">
          <Badge className={STATUS[c.status]?.[1]}>{STATUS[c.status]?.[0]}</Badge>
          <div className="border border-slate-100 rounded-xl divide-y divide-slate-100">
            {(c.items || []).map((it) => (
              <div key={it.item_id} className="flex items-center justify-between px-3 py-2 text-sm gap-2">
                <div className="min-w-0"><div className="text-slate-700 truncate">{it.item_name || it.name}</div><div className="text-xs text-slate-400">Titip {num(it.qty)} · sisa {num(it.qty_remaining ?? it.qty)} · {rupiah(it.price)}</div></div>
                {c.status !== "closed" && <input type="number" value={sold[it.item_id] ?? ""} onChange={(e) => setSold((s) => ({ ...s, [it.item_id]: e.target.value }))} placeholder="terjual" className="w-20 h-8 px-2 rounded-lg border border-slate-300 text-right" />}
              </div>
            ))}
          </div>
          {c.status !== "closed" && (
            <div className="flex gap-2">
              <Button className="flex-1" onClick={settle} disabled={busy} data-testid="consign-settle">Catat Terjual</Button>
              <Button variant="outline" className="flex-1" onClick={doReturn} disabled={busy}>Retur & Tutup</Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
